import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { createSetCookie, createClearCookie } from '../utils/cookie';
import { detectContentType } from '../utils/content-type';
import { MessageType, HttpStatus, HttpHeader, getStatusText, ErrorCode } from '../constants';
import { isIframeWritableStream } from '../stream';
import { buildExpectedAck, isExpectedAckMatch } from '../endpoint';

/**
 * Callback waiting for client acknowledgment
 */

/**
 * ServerResponse implementation
 */
export class ServerResponseImpl {
  constructor(requestId, path, serverId, peer, options) {
    _defineProperty(this, "statusCode", HttpStatus.OK);
    _defineProperty(this, "headers", {});
    _defineProperty(this, "_sent", false);
    this.requestId = requestId;
    this.path = path;
    this.serverId = serverId;
    this.peer = peer;
    this.registerStreamHandler = options === null || options === void 0 ? void 0 : options.registerStreamHandler;
    this.unregisterStreamHandler = options === null || options === void 0 ? void 0 : options.unregisterStreamHandler;
    this.heartbeat = options === null || options === void 0 ? void 0 : options.heartbeat;
    this.onSentCallback = options === null || options === void 0 ? void 0 : options.onSent;
  }

  /**
   * Send message via bound peer
   */
  sendResponseMessage(data) {
    return this.peer.sendMessage(MessageType.RESPONSE, this.requestId, data);
  }

  /**
   * Check if header exists (case-insensitive)
   */
  hasHeader(name) {
    var lower = name.toLowerCase();
    return Object.keys(this.headers).some(k => k.toLowerCase() === lower);
  }

  /**
   * Detect data type and return appropriate Content-Type
   * Returns null if Content-Type should not be auto-set
   */
  detectContentType(data) {
    return detectContentType(data, {
      checkStream: true,
      isIframeWritableStream
    });
  }

  /**
   * Auto set Content-Type based on data type (only if user not set)
   */
  ensureContentTypeIfNeeded(data) {
    if (this.hasHeader(HttpHeader.CONTENT_TYPE)) return;
    var contentType = this.detectContentType(data);
    if (contentType) {
      this.setHeader(HttpHeader.CONTENT_TYPE, contentType);
    }
  }

  /**
   * Set callback waiting for client acknowledgment
   */
  _setOnAckCallback(callback) {
    this.onAckCallback = callback;
  }

  /**
   * Trigger client acknowledgment callback
   */
  _triggerAck(received, ack) {
    if (this.onAckCallback) {
      this.onAckCallback(received, ack);
      this.onAckCallback = undefined;
    }
  }

  /**
   * Mark response as sent (and trigger onSent callback once).
   */
  markSent() {
    if (this._sent) return;
    this._sent = true;
    if (this.onSentCallback) {
      var cb = this.onSentCallback;
      this.onSentCallback = undefined;
      cb();
    }
  }

  /**
   * Internal: mark as sent for manual error responses.
   */
  _markSent() {
    this.markSent();
  }

  /**
   * Internal method: send raw data (used by send after type detection)
   */
  _sendRaw(data, options) {
    var _options$requireAck;
    if (this._sent) return Promise.resolve(false);
    this.markSent();
    var requireAck = (_options$requireAck = options === null || options === void 0 ? void 0 : options.requireAck) !== null && _options$requireAck !== void 0 ? _options$requireAck : false;
    /**
     * When requireAck is enabled, attach a unique ack payload by default so ACK can be
     * unambiguously associated with this send.
     *
     * NOTE: ack is an internal reserved field (not part of public API).
     */
    var expectedAck = buildExpectedAck(requireAck, options === null || options === void 0 ? void 0 : options.ack);
    try {
      // If acknowledgment not required, send directly and return true
      if (!requireAck) {
        this.sendResponseMessage({
          path: this.path,
          data,
          status: this.statusCode,
          statusText: getStatusText(this.statusCode),
          headers: this.headers,
          requireAck: false
        });
        return Promise.resolve(true);
      }

      // Acknowledgment required, wait for client response
      return new Promise((resolve, reject) => {
        try {
          this._setOnAckCallback((received, receivedAckMeta) => {
            if (!received) {
              resolve(false);
              return;
            }
            if (!isExpectedAckMatch(expectedAck, receivedAckMeta)) {
              resolve(false);
              return;
            }
            resolve(true);
          });
          this.sendResponseMessage({
            path: this.path,
            data,
            status: this.statusCode,
            statusText: getStatusText(this.statusCode),
            headers: this.headers,
            requireAck: true,
            ack: expectedAck
          });
        } catch (error) {
          // If window is closed, reject immediately
          if ((error === null || error === void 0 ? void 0 : error.code) === ErrorCode.TARGET_WINDOW_CLOSED) {
            reject(error);
          } else {
            throw error;
          }
        }
      });
    } catch (error) {
      // If window is closed, return rejected promise
      if ((error === null || error === void 0 ? void 0 : error.code) === ErrorCode.TARGET_WINDOW_CLOSED) {
        return Promise.reject(error);
      }
      throw error;
    }
  }

  /**
   * Universal send method - automatically detects data type and calls appropriate method
   * - If data is IframeWritableStream, calls sendStream
   * - If data is File/Blob, calls sendFile
   * - Otherwise, sends as regular data with auto-detected Content-Type
   */
  send(data, options) {
    var _this = this;
    return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function (_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            if (!_this._sent) {
              _context2.next = 1;
              break;
            }
            return _context2.abrupt("return", Promise.resolve(false));
          case 1:
            return _context2.abrupt("return", _this.peer.send({
              data,
              onStream: function () {
                var _onStream = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(stream) {
                  return _regeneratorRuntime.wrap(function (_context) {
                    while (1) switch (_context.prev = _context.next) {
                      case 0:
                        _context.next = 1;
                        return _this.sendStream(stream);
                      case 1:
                        return _context.abrupt("return", true);
                      case 2:
                      case "end":
                        return _context.stop();
                    }
                  }, _callee);
                }));
                function onStream(_x) {
                  return _onStream.apply(this, arguments);
                }
                return onStream;
              }(),
              onFileOrBlob: () => {
                // Extract options for sendFile
                var fileOptions = _objectSpread({
                  requireAck: options === null || options === void 0 ? void 0 : options.requireAck
                }, typeof File !== 'undefined' && data instanceof File ? {
                  mimeType: data.type,
                  fileName: data.name
                } : {});
                return _this.sendFile(data, fileOptions);
              },
              onOther: () => {
                // For other types, auto-detect and set Content-Type, then send
                _this.ensureContentTypeIfNeeded(data);
                return _this._sendRaw(data, options);
              }
            }));
          case 2:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }))();
  }
  json(data, options) {
    return this.send(data, options);
  }
  sendFile(content, options) {
    var _this2 = this;
    return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
      var _options$chunked, _options$autoResolve;
      return _regeneratorRuntime.wrap(function (_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            if (!_this2._sent) {
              _context3.next = 1;
              break;
            }
            return _context3.abrupt("return", false);
          case 1:
            _context3.next = 2;
            return _this2.peer.sendFile({
              content,
              fileName: options === null || options === void 0 ? void 0 : options.fileName,
              mimeType: options === null || options === void 0 ? void 0 : options.mimeType,
              chunked: (_options$chunked = options === null || options === void 0 ? void 0 : options.chunked) !== null && _options$chunked !== void 0 ? _options$chunked : false,
              chunkSize: options === null || options === void 0 ? void 0 : options.chunkSize,
              autoResolve: (_options$autoResolve = options === null || options === void 0 ? void 0 : options.autoResolve) !== null && _options$autoResolve !== void 0 ? _options$autoResolve : true,
              defaultFileName: 'file',
              defaultMimeType: 'application/octet-stream',
              onFileInfo: ({
                fileName,
                mimeType
              }) => {
                // Set file-related headers
                _this2.setHeader(HttpHeader.CONTENT_TYPE, mimeType);
                if (fileName) {
                  _this2.setHeader(HttpHeader.CONTENT_DISPOSITION, `attachment; filename="${fileName}"`);
                } else {
                  _this2.setHeader(HttpHeader.CONTENT_DISPOSITION, 'attachment');
                }
                _this2.markSent();
              },
              stream: {
                bind: {
                  requestId: _this2.requestId,
                  registerStreamHandler: _this2.registerStreamHandler,
                  unregisterStreamHandler: _this2.unregisterStreamHandler,
                  heartbeat: _this2.heartbeat,
                  serverId: _this2.serverId,
                  targetId: _this2.peer.defaultTargetId
                },
                awaitStart: true
              }
            });
          case 2:
            return _context3.abrupt("return", true);
          case 3:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    }))();
  }

  /**
   * Send stream response
   * Bind stream to current request context and start stream transmission
   */
  sendStream(stream) {
    var _this3 = this;
    return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
      return _regeneratorRuntime.wrap(function (_context4) {
        while (1) switch (_context4.prev = _context4.next) {
          case 0:
            if (!_this3._sent) {
              _context4.next = 1;
              break;
            }
            return _context4.abrupt("return");
          case 1:
            _context4.next = 2;
            return _this3.peer.sendStream({
              stream: stream,
              bind: {
                requestId: _this3.requestId,
                registerStreamHandler: _this3.registerStreamHandler,
                unregisterStreamHandler: _this3.unregisterStreamHandler,
                heartbeat: _this3.heartbeat,
                serverId: _this3.serverId,
                targetId: _this3.peer.defaultTargetId
              },
              awaitStart: true,
              beforeStart: () => {
                _this3.markSent();
              }
            });
          case 2:
          case "end":
            return _context4.stop();
        }
      }, _callee4);
    }))();
  }
  status(code) {
    this.statusCode = code;
    return this;
  }
  setHeader(name, value) {
    // Consistent with Express, returns void
    // Special handling for Set-Cookie, keep as array
    if (name.toLowerCase() === HttpHeader.SET_COOKIE.toLowerCase()) {
      var existing = this.headers[HttpHeader.SET_COOKIE];
      if (Array.isArray(value)) {
        this.headers[HttpHeader.SET_COOKIE] = Array.isArray(existing) ? [...existing, ...value] : value;
      } else {
        var newValue = String(value);
        if (Array.isArray(existing)) {
          existing.push(newValue);
        } else {
          this.headers[HttpHeader.SET_COOKIE] = [newValue];
        }
      }
    } else if (Array.isArray(value)) {
      this.headers[name] = value.join(', ');
    } else {
      this.headers[name] = String(value);
    }
  }
  set(name, value) {
    // Chainable version, compatible with Express res.set
    this.setHeader(name, value);
    return this;
  }
  cookie(name, value, options) {
    /**
     * Set Cookie (similar to HTTP Set-Cookie)
     * Generate Set-Cookie string and add to headers[HttpHeader.SET_COOKIE] array
     * Client will parse and save to cookie storage upon receiving
     */
    var setCookieStr = createSetCookie(name, value, {
      path: options === null || options === void 0 ? void 0 : options.path,
      expires: options === null || options === void 0 ? void 0 : options.expires,
      maxAge: options === null || options === void 0 ? void 0 : options.maxAge,
      httpOnly: options === null || options === void 0 ? void 0 : options.httpOnly,
      secure: options === null || options === void 0 ? void 0 : options.secure,
      sameSite: (options === null || options === void 0 ? void 0 : options.sameSite) === true ? 'Strict' : (options === null || options === void 0 ? void 0 : options.sameSite) === false ? undefined : options === null || options === void 0 ? void 0 : options.sameSite
    });

    // Add to Set-Cookie header array
    var existing = this.headers[HttpHeader.SET_COOKIE];
    if (Array.isArray(existing)) {
      existing.push(setCookieStr);
    } else {
      this.headers[HttpHeader.SET_COOKIE] = [setCookieStr];
    }
    return this;
  }
  clearCookie(name, options) {
    /**
     * Clear specified Cookie
     * Generate an expired Set-Cookie string, client will delete this cookie upon receiving
     */
    var setCookieStr = createClearCookie(name, {
      path: options === null || options === void 0 ? void 0 : options.path
    });

    // Add to Set-Cookie header array
    var existing = this.headers[HttpHeader.SET_COOKIE];
    if (Array.isArray(existing)) {
      existing.push(setCookieStr);
    } else {
      this.headers[HttpHeader.SET_COOKIE] = [setCookieStr];
    }
    return this;
  }
}